/**
 * RootCausesTreemap - Treemap visualization of root cause distribution
 *
 * Atomic level: Organism
 * Used in: CollectionHeader to show root cause breakdown
 *
 * Design: Purple gradient boxes sized by percentage
 */

import React from 'react';
import { cn } from '../../../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../../../ui/tooltip';
import type { RootCauseCategory } from '../../../../types/record';

interface RootCausesTreemapProps {
  data: RootCauseCategory[];
  className?: string;
}

// Multi-hue palette (purple / teal / berry) with varying opacities to echo
// the prototype's segmented look. Index 0..5 maps from top to bottom block.
const TREEMAP_COLORS = [
  'bg-purple',          // top-left: largest block
  'bg-purple/75',       // top-right: second largest
  'bg-purple/55',       // row 2 first
  'bg-teal/70',         // row 2 second
  'bg-teal/50',         // row 2 third
  'bg-pink/60',         // row 2 fourth
];

interface TreemapItemProps {
  name: string;
  percentage: number;
  colorIndex: number;
  size?: 'lg' | 'sm'; // lg = labelled block (rows 1), sm = pct-only (row 2)
  flex: number;       // weighted flex basis from percentage
}

const TreemapItem: React.FC<TreemapItemProps> = ({
  name,
  percentage,
  colorIndex,
  size = 'lg',
  flex,
}) => {
  const bgColor = TREEMAP_COLORS[Math.min(colorIndex, TREEMAP_COLORS.length - 1)];

  const block = size === 'sm' ? (
    <div
      className={cn('rounded-sm px-1.5 flex items-end justify-start text-white cursor-default', bgColor)}
      style={{ flex: `${flex} 1 0` }}
    >
      <span className="text-[9px] font-bold leading-tight pb-0.5">
        {percentage}%
      </span>
    </div>
  ) : (
    <div
      className={cn('rounded-sm p-1.5 flex flex-col justify-end text-white cursor-default', bgColor)}
      style={{ flex: `${flex} 1 0` }}
    >
      <span className="text-[9px] font-semibold leading-tight truncate">
        {name}
      </span>
      <span className="text-sm font-bold leading-none mt-0.5">
        {percentage}%
      </span>
      <span className="text-[7px] opacity-90 mt-0.5">Volume</span>
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{block}</TooltipTrigger>
      <TooltipContent side="top">
        <span className="font-medium">{name}</span>
        <span className="text-muted-foreground ml-1">— {percentage}%</span>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Treemap: top-2 large labelled blocks weighted by percentage, plus a row of
 * up to 4 smaller blocks showing just the percentage. Mirrors the monitor
 * prototype (~/Downloads/monitor-surface/prototypes/collection-gateway.html).
 */
const RootCausesTreemap: React.FC<RootCausesTreemapProps> = ({
  data,
  className,
}) => {
  const sortedData = [...data].sort((a, b) => b.percentage - a.percentage);
  const top2 = sortedData.slice(0, 2);
  const next4 = sortedData.slice(2, 6);

  if (top2.length < 1) {
    return (
      <div className={cn('w-full h-24 rounded bg-muted/40', className)}>
        <p className="text-muted-foreground text-xs p-3">No root cause data</p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className={cn('flex flex-col gap-0.5 w-full', className)}>
      {/* Row 1: top 2 categories, weighted by percentage */}
      <div className="flex gap-0.5 h-[70px]">
        {top2.map((item, i) => (
          <TreemapItem
            key={item.name}
            name={item.name}
            percentage={item.percentage}
            colorIndex={i}
            size="lg"
            flex={Math.max(item.percentage, 1)}
          />
        ))}
      </div>

      {/* Row 2: next 3-4 categories, weighted, percentage-only */}
      {next4.length > 0 && (
        <div className="flex gap-0.5 h-[40px]">
          {next4.map((item, i) => (
            <TreemapItem
              key={item.name}
              name={item.name}
              percentage={item.percentage}
              colorIndex={i + 2}
              size="sm"
              flex={Math.max(item.percentage, 1)}
            />
          ))}
        </div>
      )}
    </div>
    </TooltipProvider>
  );
};

export { RootCausesTreemap };
